import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { MdCleaningServices, MdOutlineWaterDrop, MdDelete } from 'react-icons/md';
import { GiBroom, GiVacuumCleaner, GiWindow } from 'react-icons/gi';
import { FaHouseChimney } from 'react-icons/fa6';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';

const cleaningServices = [
  {
    icon: <FaHouseChimney className="h-8 w-8" />,
    title: 'General House Cleaning',
    description: 'Comprehensive cleaning for homes of all sizes.',
  },
  {
    icon: <GiBroom className="h-8 w-8" />,
    title: 'End-of-Lease Cleaning',
    description: 'Thorough cleaning to help secure your bond return.',
  },
  {
    icon: <GiWindow className="h-8 w-8" />,
    title: 'Window Cleaning',
    description: 'Crystal clear windows inside and out.',
  },
  {
    icon: <MdOutlineWaterDrop className="h-8 w-8" />,
    title: 'Pressure Washing',
    description: 'High-pressure cleaning for driveways, decks, and more.',
  },
  {
    icon: <MdCleaningServices className="h-8 w-8" />,
    title: 'Gutter Cleaning',
    description: 'Keep your gutters clear and flowing freely.',
  },
  {
    icon: <GiVacuumCleaner className="h-8 w-8" />,
    title: 'One-Off Deep Cleans',
    description: 'Intensive cleaning for special occasions or spring cleaning.',
  },
  {
    icon: <MdDelete className="h-8 w-8" />,
    title: 'Hard Waste Removal',
    description: 'Removal and disposal of bulky items, furniture, and household waste.',
  },
];

/**
 * Cleaning services page
 */
export function Cleaning() {
  return (
    <>
      {/* Page Header */}
      <section className="section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="category-label">Indoor Services</span>
            <h1 className="text-4xl font-bold md:text-5xl">
              Cleaning Services
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Professional cleaning services for homes and businesses in Tasmania.
              From regular maintenance to deep cleans, I deliver spotless results
              every time.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Request Quote
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/services">All Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding" aria-labelledby="cleaning-services">
        <div className="container-custom">
          <div className="mb-12">
            <span className="category-label">What's Included</span>
            <h2 id="cleaning-services" className="text-3xl font-bold">
              Our Cleaning Services
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cleaningServices.map((service, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="pt-6">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-muted text-primary">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold">{service.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist & Pricing Section */}
      <section className="section-padding bg-card" aria-labelledby="cleaning-checklist">
        <div className="container-custom">
          {/* Feature Card - GitBook style */}
          <Card className="overflow-hidden rounded-2xl border-0 bg-muted/50">
            <div className="grid lg:grid-cols-5">
              {/* Left Content - What to Expect */}
              <div className="p-8 lg:col-span-3 lg:p-12">
                <span className="category-label">Standard Clean Includes</span>
                <h2 id="cleaning-checklist" className="mt-2 text-3xl font-bold">
                  What to Expect
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Every cleaning service includes these essential tasks as standard.
                  Additional services available upon request.
                </p>
                <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                  {[
                    'Dusting all surfaces',
                    'Vacuuming floors',
                    'Mopping hard floors',
                    'Kitchen cleaning',
                    'Bathroom sanitising',
                    'Mirror & glass cleaning',
                    'Rubbish removal',
                    'Bed making (optional)',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </div>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Content - Pricing */}
              <div className="flex items-center justify-center border-t border-border/50 bg-gradient-to-br from-primary/5 to-primary/10 p-8 lg:col-span-2 lg:border-l lg:border-t-0 lg:p-12">
                <div className="text-center">
                  <span className="category-label">Pricing</span>
                  <h3 className="mt-2 text-2xl font-bold">Price Upon Request</h3>
                  <p className="mx-auto mt-4 max-w-xs text-sm text-muted-foreground">
                    Pricing depends on the size of your space and specific requirements.
                    Contact me for a free consultation and quote.
                  </p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to="/contact">
                      Get Your Quote
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
